import { useInfiniteQuery } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { wrapperResult } from "@api/osp";
import { Stock, StockPagination } from "../features/api/openData";

import { QUERY_KEYS } from "../config";
import { rootStore } from "../store";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { GetRankingStocksParams as StocksQueryParams } from "@/src/types/api-params";

interface StocksRankingParams {
  queryParams?: StocksQueryParams;
  enabled?: boolean;
  queryKey?: string[];
  staleTime?: number;
}

// 定义 TokensQuestParams 的具体类型
export const StocksQuestParams = {
  EnRich_Market: "enrich.with_market.enable" as const,
  EnRich_Enable: "enrich.enable" as const,
  Enrich_With_SparkLine_Enable: "enrich.with_sparkline.enable" as const,
  Enrich_With_SparkLine_Interval: "enrich.with_sparkline.interval" as const,
  Enrich_With_Rating: "enrich.with_rating" as const,
  Enrich_With_SparkLine_Trading_Period:
    "enrich.with_sparkline.trading_period" as const,
  Enrich_With_Trading_Data: "enrich.with_trading_data" as const,
  Sort_Order: "sort.order" as const,
  Sort_Field: "sort.field" as const,
} as const;

// 定义查询参数的精确类型
const defaultLimit = 10;

const defaultQueryParams = {
  offset: 0,
  limit: defaultLimit,
  // "sort.field": "PRICE_CHANGE_PERCENTAGE",
};
export const useStocksRankingInfiniteScroll = (
  params?: StocksRankingParams,
) => {
  const { openDataApi } = useOspApiClientProvider();

  /**
   * 获取登录状态用于缓存键
   *
   * 问题分析：
   * - 未登录时请求了部分数据（如第1页）
   * - 登录后用户权限变化，可能看到不同的数据
   * - 需要清空缓存，重新从第0页开始请求
   *
   * 解决方案：
   * - 在 queryKey 中包含登录状态
   * - 登录状态变化时自动触发缓存失效
   * - 确保登录后重新从头开始加载数据
   */
  const { isLoggedIn } = useSnapshot(rootStore.login);

  /**
   * 关键修复：包含登录状态的查询键
   *
   * 这确保了：
   * 1. 未登录时的缓存：[STOCK_DASHBOARD, params, false]
   * 2. 登录后的缓存：[STOCK_DASHBOARD, params, true]
   * 3. 两者是不同的缓存，登录时会自动重新请求
   */
  const queryKey = params?.queryKey
    ? [QUERY_KEYS.STOCK_DASHBOARD, ...params?.queryKey]
    : [
        QUERY_KEYS.STOCK_DASHBOARD,
        params?.queryParams,
        isLoggedIn, // 👈 关键：包含登录状态
      ];

  const initialPageParam = {
    ...defaultQueryParams,
    ...params?.queryParams,
    market: params?.queryParams?.market,
  };

  return useInfiniteQuery({
    queryKey: queryKey,
    staleTime: params?.staleTime ?? 60 * 1000,
    queryFn: async ({ pageParam }) => {
      if (!openDataApi) {
        throw new Error("OpenDataApi is not available");
      }
      try {
        const response = await wrapperResult(
          openDataApi!.stocks.getRankingStocks(pageParam),
        );
        return response as StockPagination;
      } catch (error) {
        throw new Error("Failed to fetch stocks ranking");
      }
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (
        lastPageParam?.limit === lastPage.rows?.length &&
        typeof lastPageParam?.offset === "number"
      ) {
        const nextParams = {
          ...lastPageParam,
          offset: lastPageParam?.offset + (lastPageParam.limit ?? defaultLimit),
        };

        return nextParams;
      }
      return undefined;
    },
    select: (data): Stock[] | undefined => {
      return (
        data?.pages
          ?.flatMap((page) => page.rows || [])
          .filter((stock): stock is Stock => !!stock) || undefined
      );
    },
    initialPageParam: initialPageParam,
    /**
     * 启用条件优化：考虑登录状态稳定性
     *
     * 确保：
     * 1. API 客户端已就绪
     * 2. 登录状态稳定（不在加载中）
     * 3. 用户明确启用查询
     *
     * 这避免了登录状态变化过程中的重复请求
     */
    enabled: !!openDataApi && (params?.enabled ?? true),
  });
};
